// eslint-disable-next-line no-console
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getScreen, processStoreId } from '../../components/Availability/DeliveryOptions/DeliveryOptions.Helper';
import { getOperatorIcon } from '../../components/Availability/DeliveryOptions/utils';
import apiConfig from '../../config/api/apiConfig';
import constants from '../../config/constants';
import httpService from '../httpService';
import Logger from '../Logger';
import { generateDeliveryText } from './useCurrentDeliveryText';

const { AVAILABILITY_PICKUPINSTORE, AVAILABILITY_STOCKINSTORE, AVAILABILITY_OUTOFSTOCK } = constants;

const useDeliveryOptions = ({
  type,
  regionCode,
  isDefaultComuna,
  labelsData,
  modalVisible,
  zoneConfig,
  setZoneConfig,
  sizes,
  colorVariants,
  selectedDeliveryOption,
  setSelectedDeliveryOption,
  isSizeSelected,
  deliveryData,
  deliveryActions,
  productData,
  variantData,
  deliveryErrors,
  outOfStock,
}) => {
  const { isFreeShippingApplicable, offeringId, sellerId, stockCategoryConfig, stockCategoryLimit } = variantData || {};
  const { variants } = productData || {};
  const pickupInStoreData = deliveryData?.pickupInStoreData || [];
  const homeDeliveryData = deliveryData?.homeDeliveryData || [];
  const storesList = deliveryData?.storesList || [];

  const setPickupInStoreData = deliveryActions?.setPickupInStoreData || (() => {});
  const setHomeDeliveryData = deliveryActions?.setHomeDeliveryData || (() => {});
  const setStoresList = deliveryActions?.setStoresList || (() => {});
  const setDeliveryError = deliveryActions?.setDeliveryError || (() => {});

  const [selectedStoreDetails, setSelectedStoreDetails] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(selectedDeliveryOption?.storeId);
  const [selectedStoreName, setSelectedStoreName] = useState('');
  const isError = deliveryErrors || {
    PickupInStore: { isError: false, code: null },
    HomeDelivery: { isError: false, code: null },
    StockInStore: { isError: false, code: null },
  };
  const [comunaName, setComunaName] = useState('');
  const [isCambiar, setIsCambiar] = useState(false);
  const [isZoneSet, setIsZoneSet] = useState(false);
  const [parentStateName, setParentStateName] = useState('');
  const [isModalBusy, setModalBusy] = useState(false);
  const [isMapView, setIsMapView] = useState(false);

  const stableDeliveryActions = useMemo(
    () => ({
      setPickupInStoreData,
      setHomeDeliveryData,
      setStoresList,
      setDeliveryError,
    }),
    [setPickupInStoreData, setHomeDeliveryData, setStoresList, setDeliveryError]
  );

  const defaultScreen = getScreen({
    type,
    modalVisible,
    variants,
    colorVariants,
    sizes,
    isSizeSelected,
    isZoneSet,
    comunaName: 'Cerrillos',
    pickupInStoreData,
    storesList,
    homeDeliveryData,
    isError,
  });

  const [screen, setScreen] = useState(defaultScreen);

  useEffect(() => {
    if (screen === 'screen3') {
      if (type === AVAILABILITY_PICKUPINSTORE || type === AVAILABILITY_STOCKINSTORE) {
        if (!isError[type].isError) {
          setIsMapView(true);
        }
      } else {
        setIsMapView(false);
      }
    }
  }, [screen, type]);

  useEffect(() => {
    if (!modalVisible) {
      // reset states when modal is closed
      setScreen('screen1');
      setIsMapView(false);
      setModalBusy(false);
      setIsCambiar(false);
    }
  }, [modalVisible, type]);

  const storeData = useMemo(() => {
    if (storesList.length === 0 && productData.pickupInStoreData && productData.pickupInStoreData.length > 0) {
      return productData.pickupInStoreData;
    }

    const mappedStoreList = storesList.map(({ id, address, storeName, stockQuantity }) => {
      const { latitude, longitude } = address;
      return {
        storeId: id,
        storeName,
        latitude,
        longitude,
        operator: getOperatorIcon(storeName),
        address,
        stockQuantity,
      };
    });

    return { [AVAILABILITY_STOCKINSTORE]: mappedStoreList, [AVAILABILITY_PICKUPINSTORE]: pickupInStoreData };
  }, [storesList, pickupInStoreData, productData.pickupInStoreData]);

  const getCoordinates = (source) => {
    const latitude =
      _.get(source, 'latitude') || _.get(source, 'comuna.latitude') || _.get(source, 'comuna.data.latitude') || 0;

    const longitude =
      _.get(source, 'longitude') || _.get(source, 'comuna.longitude') || _.get(source, 'comuna.data.longitude') || 0;

    return { latitude, longitude };
  };

  const dispatchAvailabilityMetrics = (stores, comuna) => {
    const selectedComuna = _.get(comuna, 'name', '');
    const selectedRegion = _.get(comuna, 'parentStateName', '');
    const eventToDigitalData = new CustomEvent('DDPDPStoreAvailabilityMetrics', {
      bubbles: true,
      detail: {
        isStoreDataAvailable: stores.length > 0,
        selectedZone: {
          comuna: selectedComuna,
          region: selectedRegion,
        },
      },
    });
    window.dispatchEvent(eventToDigitalData);
  };

  const fetchStores = useCallback(
    async ({ comuna }) => {
      const { latitude, longitude } = getCoordinates(comuna || zoneConfig?.comuna || zoneConfig || {});
      const url = apiConfig.getStoreAvailabilityUrl({
        offeringId,
        sellerId,
        latitude,
        longitude,
        regionCode,
      });
      try {
        const response = await httpService().get(url, {
          headers: {
            'x-commerce': 'Falabella',
            'x-cmref': 'product-discovery-web',
          },
        });
        const errors = _.get(response, 'data.alert.errors', []);
        const stores = _.get(response, 'data.stores', []);
        if (stores.length && response.data && !errors.length) {
          stableDeliveryActions.setStoresList(stores);
          setSelectedStoreId(_.get(stores, '[0].id', null));
          stableDeliveryActions.setDeliveryError(AVAILABILITY_STOCKINSTORE, false);
          processStoreId({
            storeId: stores[0].id || null,
            storesData: stores,
            setSelectedStoreId,
            setSelectedStoreDetails,
            setSelectedStoreName,
            regionCode,
            zoneConfig,
            isFreeShippingApplicable,
            setSelectedDeliveryOption,
            parentStateName: comuna.parentStateName,
            type: AVAILABILITY_STOCKINSTORE,
            stockCategoryConfig,
            stockCategoryLimit,
            textDictionary: labelsData,
            isDefaultComuna,
            outOfStock,
            setTextGenerated: deliveryActions.setTextGenerated,
          });
        } else if ((errors && errors.length) || stores.length === 0) {
          setComunaName(_.get(comuna, 'name', ''));
          stableDeliveryActions.setDeliveryError(
            AVAILABILITY_STOCKINSTORE,
            true,
            _.get(errors, '[0].code', 'UNKNOWN_ERROR')
          );
          const selectionData = {
            selectedType: 'Sin stock',
            date: '',
            month: '',
            price: '',
            locationName: comuna.name.toLocaleLowerCase(),
            storeName: selectedDeliveryOption.storeName,
            politicalId: selectedDeliveryOption.politicalId,
            storeId: selectedDeliveryOption.storeId,
            isError: true,
            isFreeShippingApplicable: false,
            deliveryOptionType: AVAILABILITY_STOCKINSTORE,
            parentStateName: comuna.parentStateName,
          };
          setSelectedDeliveryOption(selectionData);
          const stockText = generateDeliveryText(
            selectionData,
            AVAILABILITY_OUTOFSTOCK,
            labelsData,
            AVAILABILITY_STOCKINSTORE
          );
          deliveryActions.setTextGenerated((prev) => ({ ...prev, [AVAILABILITY_STOCKINSTORE]: stockText }));
        }
        dispatchAvailabilityMetrics(stores, comuna);
      } catch (error) {
        Logger.error(`store availability fetch failed: ${error}`);
      }
    },
    [
      type,
      zoneConfig,
      sizes,
      colorVariants,
      isSizeSelected,
      variants,
      offeringId,
      sellerId,
      regionCode,
      stableDeliveryActions,
      selectedDeliveryOption,
      setSelectedDeliveryOption,
    ]
  );

  const saveZones = useCallback(
    ({ dataZoneConfig, isHomeDelivery, isPickupInStore, isStockInStore }) => {
      setZoneConfig({
        ...dataZoneConfig,
      });

      if (isHomeDelivery) {
        setScreen('screen3');
      } else if (isPickupInStore) {
        setScreen('screen3');
      } else if (isStockInStore) {
        setScreen('screen3');
        setIsZoneSet(true);
      }
    },
    [type, isError, setZoneConfig]
  );

  return {
    screen,
    homeDeliveryData,
    selectedStoreDetails,
    selectedStoreId,
    selectedStoreName,
    isError,
    comunaName,
    isCambiar,
    isZoneSet,
    storeData,
    storesList,
    parentStateName,
    isModalBusy,
    isMapView,
    setIsMapView,
    setScreen,
    setHomeDeliveryData,
    setPickupInStoreData,
    setSelectedStoreDetails,
    setSelectedStoreId,
    setSelectedStoreName,
    setComunaName,
    setIsCambiar,
    setIsZoneSet,
    fetchStores,
    saveZones,
    setStoresList,
    setModalBusy,
    setParentStateName,
    setDeliveryError,
    saveSelectedStoreOption: () => {
      const selectedStoreData = type === constants.AVAILABILITY_PICKUPINSTORE ? pickupInStoreData : storesList;
      processStoreId({
        storeId: selectedStoreId,
        storesData: selectedStoreData,
        setSelectedStoreId,
        setSelectedStoreDetails,
        setSelectedStoreName,
        regionCode,
        zoneConfig,
        isFreeShippingApplicable,
        setSelectedDeliveryOption,
        parentStateName,
        type,
        stockCategoryConfig,
        stockCategoryLimit,
        textDictionary: labelsData,
        isDefaultComuna,
        outOfStock,
        setTextGenerated: deliveryActions.setTextGenerated,
      });
    },
  };
};

export default useDeliveryOptions;
